import axiosInstance from '../auth/axiosInstance';
import { getApiErrorMessage } from '../types/api';
import type {
  AssessmentBlueprintDetail,
  AssessmentBlueprintSummary,
  SaveAssessmentBlueprintPayload,
} from '../types/assessmentBlueprint';
import { normalizeAssessmentBlueprintDetail } from '../utils/assessmentBlueprintUtils';

const throwIfFailed = (result: { code?: number; message?: string; data?: unknown } | null | undefined, fallback: string) => {
  if (result?.code && result.code >= 400) {
    throw new Error(result.message || fallback);
  }
  return result?.data ?? result;
};

export const getAssessmentBlueprints = async (
  pageNumber = 1,
  pageSize = 10,
  sort = 'date-desc',
  search = '',
  status = ''
) => {
  try {
    const { data: result } = await axiosInstance.get('/AssessmentBlueprint/GetAssessmentBlueprints', {
      params: { pageNumber, pageSize, sort, search, status: status || undefined },
    });

    if (
      result?.data &&
      Array.isArray(result.data.items) &&
      typeof result.data.totalCount === 'number'
    ) {
      const { items, totalCount, pageNumber: returnedPage, pageSize: returnedSize } = result.data;
      return {
        items: items as AssessmentBlueprintSummary[],
        totalCount,
        pageNumber: returnedPage,
        pageSize: returnedSize,
      };
    }

    return { items: [], totalCount: 0, pageNumber, pageSize };
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const getAssessmentBlueprint = async (id: number): Promise<AssessmentBlueprintDetail> => {
  try {
    const { data: result } = await axiosInstance.get(`/AssessmentBlueprint/GetAssessmentBlueprint/${id}`);
    return normalizeAssessmentBlueprintDetail(throwIfFailed(result, 'Failed to load assessment'));
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const createAssessmentBlueprintFromFramework = async (payload: {
  traxccelFrameworkId: number;
  title?: string;
  description?: string;
  createdBy?: number;
}): Promise<AssessmentBlueprintDetail> => {
  try {
    const { data: result } = await axiosInstance.post(
      '/AssessmentBlueprint/CreateFromTraxccelFramework',
      payload
    );
    return normalizeAssessmentBlueprintDetail(throwIfFailed(result, 'Failed to create assessment'));
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const copyAssessmentBlueprint = async (payload: {
  sourceBlueprintId: number;
  title: string;
  projectId: number;
  createdBy?: number;
}): Promise<AssessmentBlueprintDetail> => {
  try {
    const { data: result } = await axiosInstance.post(
      '/AssessmentBlueprint/CopyAssessmentBlueprint',
      payload
    );
    return normalizeAssessmentBlueprintDetail(throwIfFailed(result, 'Failed to copy assessment'));
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const saveAssessmentBlueprint = async (
  payload: SaveAssessmentBlueprintPayload
): Promise<AssessmentBlueprintDetail> => {
  try {
    const { data: result } = await axiosInstance.post(
      '/AssessmentBlueprint/SaveAssessmentBlueprint',
      payload
    );
    return normalizeAssessmentBlueprintDetail(throwIfFailed(result, 'Failed to save assessment'));
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const publishAssessmentBlueprint = async (
  id: number,
  publishedBy?: number
): Promise<AssessmentBlueprintDetail> => {
  try {
    const { data: result } = await axiosInstance.post(
      `/AssessmentBlueprint/PublishAssessmentBlueprint/${id}`,
      { id, publishedBy }
    );
    return normalizeAssessmentBlueprintDetail(throwIfFailed(result, 'Failed to publish assessment'));
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const deleteAssessmentBlueprint = async (id: number) => {
  try {
    const { data: result } = await axiosInstance.post(
      `/AssessmentBlueprint/DeleteAssessmentBlueprint/${id}`
    );
    return throwIfFailed(result, 'Failed to delete assessment');
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};
